import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Spinner, Alert, Form, Badge } from 'react-bootstrap';
import budgetService from '../../../services/budgetService';
import objectivesService from '../../../services/objectivesService';
import { useAuth } from '../../../contexts/AuthContext';

const PLATFORM_COLORS = {
  META: '#1877f2',
  GOOGLE: '#34a853',
  TIKTOK: '#ff0050',
  LINKEDIN: '#0A66C2',
  PROGRAMMATIC: '#7c3aed',
};

const fmt = (n, dec = 0) => {
  if (n == null || isNaN(n)) return '0';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtCurrency = (n) => '$' + fmt(n, 2);
const fmtPct = (n) => fmt(n, 1) + '%';
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const GaugeChart = ({ value, label, color = '#0d6efd', size = 120 }) => {
  const pct = clamp(value, 0, 100);
  const angle = (pct / 100) * 180;
  const rad = (a) => (a * Math.PI) / 180;
  const r = size / 2 - 10;
  const cx = size / 2;
  const cy = size / 2 + 5;

  const x1 = cx - r;
  const x2 = cx + r;
  const endX = cx + r * Math.cos(Math.PI - rad(angle));
  const endY = cy - r * Math.sin(Math.PI - rad(angle));
  const largeArc = angle > 180 ? 1 : 0;

  return (
    <div className="text-center">
      <svg width={size} height={size / 2 + 25} viewBox={`0 0 ${size} ${size / 2 + 25}`}>
        <path d={`M ${x1} ${cy} A ${r} ${r} 0 0 1 ${x2} ${cy}`} fill="none" stroke="#e9ecef" strokeWidth="10" strokeLinecap="round" />
        <path d={`M ${x1} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY}`} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="20" fontWeight="700" fill="#2c3e50">{fmtPct(value)}</text>
      </svg>
      {label && <div className="small text-muted mt-1">{label}</div>}
    </div>
  );
};

const ProgressBar = ({ value, color = '#0d6efd' }) => {
  const pct = clamp(value, 0, 100);
  return (
    <div style={{ position: 'relative', height: 22, borderRadius: 11, background: '#e9ecef', overflow: 'hidden', minWidth: 80 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 11, transition: 'width 0.6s ease' }} />
      <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 11, fontWeight: 600, color: pct > 50 ? '#fff' : '#333' }}>{fmtPct(value)}</span>
    </div>
  );
};

const MiniDonut = ({ value, color = '#0d6efd', size = 60, label }) => {
  const pct = clamp(value, 0, 100);
  const r = 22;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="text-center">
      <svg width={size} height={size} viewBox="0 0 60 60">
        <circle cx="30" cy="30" r={r} fill="none" stroke="#e9ecef" strokeWidth="6" />
        <circle cx="30" cy="30" r={r} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 30 30)" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
        <text x="30" y="33" textAnchor="middle" fontSize="11" fontWeight="700" fill="#2c3e50">{Math.round(pct)}%</text>
      </svg>
      {label && <div style={{ fontSize: 10, color: '#6c757d', marginTop: -4 }}>{label}</div>}
    </div>
  );
};

const KpiCard = ({ title, value, subtitle, color = '#1B2A4A' }) => (
  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: `4px solid ${color}` }}>
    <Card.Body className="py-2 px-3">
      <div className="text-muted small">{title}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {subtitle && <div className="text-muted" style={{ fontSize: 11 }}>{subtitle}</div>}
    </Card.Body>
  </Card>
);

const MonthBarChart = ({ data }) => {
  if (!data || data.length === 0) return <div className="text-muted small text-center py-4">Sin datos</div>;
  const maxVal = Math.max(...data.map(d => Math.max(d.invReal || 0, d.invProy || 0, d.kpiReal || 0, d.kpiProy || 0)), 1);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div className="d-flex align-items-end justify-content-around" style={{ minHeight: 140, gap: 4 }}>
        {data.map((m, i) => {
          const h = (v) => Math.max((v / maxVal) * 120, 2);
          return (
            <div key={i} className="text-center" style={{ flex: 1, minWidth: 50 }}>
              <div className="d-flex align-items-end justify-content-center" style={{ height: 120, gap: 2 }}>
                <div style={{ width: 10, height: h(m.invReal), background: '#1B2A4A', borderRadius: 2 }} title={`Inversión Real: ${fmtCurrency(m.invReal)}`} />
                <div style={{ width: 10, height: h(m.invProy), background: '#4dabf7', borderRadius: 2 }} title={`Inversión Proy: ${fmtCurrency(m.invProy)}`} />
                <div style={{ width: 10, height: h(m.kpiReal), background: '#51cf66', borderRadius: 2 }} title={`KPI Real: ${fmt(m.kpiReal)}`} />
                <div style={{ width: 10, height: h(m.kpiProy), background: '#fcc419', borderRadius: 2 }} title={`KPI Proy: ${fmt(m.kpiProy)}`} />
              </div>
              <div className="small text-muted mt-1" style={{ fontSize: 10 }}>{m.month}</div>
            </div>
          );
        })}
      </div>
      <div className="d-flex justify-content-center gap-3 mt-2" style={{ fontSize: 10 }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#1B2A4A', borderRadius: 2, marginRight: 3 }} />Inversión</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#4dabf7', borderRadius: 2, marginRight: 3 }} />Inversión Proy</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#51cf66', borderRadius: 2, marginRight: 3 }} />KPIs</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#fcc419', borderRadius: 2, marginRight: 3 }} />KPIs Proy</span>
      </div>
    </div>
  );
};

//Correción 2: DailyLeadsCplChart
const DailyLeadsCplChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div className="text-muted small text-center py-4">Sin datos</div>;
  }

  const series = data
    .map(d => {
      const leads = d.leads || 0;
      const cost = d.cost || 0;
      const cpl = leads > 0 ? cost / leads : 0;
      return {
        date: d.date,
        leads,
        cpl,
      };
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const maxLeads = Math.max(...series.map(s => s.leads), 1);
  const maxCpl = Math.max(...series.map(s => s.cpl), 1);
  
  // Línea CPL (path SVG)
	const cplPath = series
	  .map((s, i) => {
		const x = i * 50 + 30; // espaciado horizontal
		const y = 150 - (s.cpl / maxCpl) * 120; // escala vertical
		return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
	  })
	  .join(' ');

  return (
    <div style={{ overflowX: 'auto' }}>
      <div className="d-flex align-items-end justify-content-between position-relative" style={{ minHeight: 180, gap: 6 }}>
	  <svg
		  width={series.length * 50}
		  height={160}
		  style={{
			position: 'absolute',
			left: 0,
			top: 0,
			pointerEvents: 'none',
		  }}
		>
		  <path
			d={cplPath}
			fill="none"
			stroke="#F59F00"
			strokeWidth="2"
		  />
		</svg>
        {series.map((s, i) => {
          const hLeads = Math.max((s.leads / maxLeads) * 140, 2);
          const yCpl = Math.max((s.cpl / maxCpl) * 140, 2);
          return (
            <div key={i} className="text-center" style={{ minWidth: 44 }}>
              <div style={{ height: 140, position: 'relative' }}>
                <div
                  title={`Leads: ${fmt(s.leads)}`}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: '35%',
                    width: 14,
                    height: hLeads,
                    background: '#1B2A4A',
                    borderRadius: 3,
                  }}
                />
              </div>
              <div className="small text-muted mt-1" style={{ fontSize: 10 }}>
                {s.date.slice(5)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="d-flex justify-content-center gap-4 mt-2" style={{ fontSize: 11 }}>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: '#1B2A4A', marginRight: 6 }} />
          Leads (diario)
        </span>
        <span>
          <span style={{ display: 'inline-block', width: 10, height: 2, background: '#F59F00', marginRight: 6 }} />
          CPL
        </span>
      </div>
    </div>
  );
};


const FordResultadosDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [investmentCompliance, setInvestmentCompliance] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, objResult, invResult] = await Promise.all([
        budgetService.getCampaignDashboardMetrics({ dateFrom, dateTo, clientId: 1 }),
        objectivesService.getDashboardSummary({ clientId: 1, dateFrom, dateTo }).catch(() => null),
        budgetService.getInvestmentCompliance({ clientId: 1, dateFrom, dateTo }).catch(() => null),
      ]);
      setData(result?.data || result);
      if (objResult?.success !== false) setComplianceData(objResult?.data || objResult);
      if (invResult?.success !== false && invResult?.data) setInvestmentCompliance(invResult.data);
    } catch (err) {
      setError(err.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const processed = useMemo(() => {
    if (!data) return null;
    const campaigns = data.campaigns || [];
    const kpis = data.kpis || {};

    const byPlatform = {};
    const byModelo = {};
    for (const c of campaigns) {
      const plat = (c.sourcePlatform || c.platform || 'OTRO').toUpperCase();
      if (!byPlatform[plat]) byPlatform[plat] = { impressions: 0, clicks: 0, cost: 0, leads: 0, conversions: 0, videoViews: 0, reach: 0 };
      byPlatform[plat].impressions += c.impressions || 0;
      byPlatform[plat].clicks += c.clicks || 0;
      byPlatform[plat].cost += c.cost || 0;
      byPlatform[plat].leads += c.leads || 0;
      byPlatform[plat].conversions += c.conversions || 0;
      byPlatform[plat].videoViews += c.videoViews || 0;
      byPlatform[plat].reach += c.reach || 0;

      const name = (c.campaignName || c.name || '').toUpperCase();
      const modeloMatch = name.match(/_(EVEREST|ESCAPE|TERRITORY|BRONCO|RANGER|MAVERICK|EXPLORER|EDGE|MUSTANG|ECOSPORT|TRANSIT|F-150|PUMA)/i);
      if (modeloMatch) {
        const mod = modeloMatch[1].charAt(0).toUpperCase() + modeloMatch[1].slice(1).toLowerCase();
        if (!byModelo[mod]) byModelo[mod] = { impressions: 0, clicks: 0, cost: 0, leads: 0, conversions: 0 };
        byModelo[mod].impressions += c.impressions || 0;
        byModelo[mod].clicks += c.clicks || 0;
        byModelo[mod].cost += c.cost || 0;
        byModelo[mod].leads += c.leads || 0;
        byModelo[mod].conversions += c.conversions || 0;
      }
    }

    const totalCost = Object.values(byPlatform).reduce((s, p) => s + p.cost, 0);
    const totalLeads = Object.values(byPlatform).reduce((s, p) => s + p.leads, 0);
    const totalImpressions = Object.values(byPlatform).reduce((s, p) => s + p.impressions, 0);
    const totalClicks = Object.values(byPlatform).reduce((s, p) => s + p.clicks, 0);

    const monthlyData = (data.dailyTrend || data.monthlyTrend || []).reduce((acc, d) => {
      const monthKey = (d.date || d.dateKey?.toString() || '').substring(0, 7);
      if (!monthKey) return acc;
      if (!acc[monthKey]) acc[monthKey] = { month: monthKey, invReal: 0, kpiReal: 0, invProy: 0, kpiProy: 0 };
      acc[monthKey].invReal += d.cost || 0;
      acc[monthKey].kpiReal += d.leads || d.conversions || 0;
      return acc;
    }, {});

    return {
      kpis,
      totalCost,
      totalLeads,
      totalImpressions,
      totalClicks,
      byPlatform,
      byModelo,
      monthlyData: Object.values(monthlyData).slice(-6),
      campaignCount: campaigns.length,
    };
  }, [data]);

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
      <Spinner animation="border" variant="primary" /><span className="ms-2">Cargando Dashboard de Resultados...</span>
    </div>
  );

  if (error) return <Alert variant="danger" className="m-3">{error}</Alert>;
  if (!processed) return <Alert variant="info" className="m-3">No hay datos disponibles</Alert>;

  const { totalCost, totalLeads, totalImpressions, totalClicks, byPlatform, byModelo, monthlyData } = processed;

  const invBudgeted = investmentCompliance?.totalBudgeted || 0;
  const invReal = investmentCompliance?.totalReal || totalCost;
  const cumplimientoInversion = invBudgeted > 0 ? (invReal / invBudgeted) * 100 : 0;

  const aggregated = complianceData?.aggregatedByUnit || {};
  const objectives = complianceData?.objectives || [];
  const leadsObj = aggregated['Leads'];
  const cumplimientoKpi = leadsObj && leadsObj.objective > 0 ? (leadsObj.real / leadsObj.objective) * 100 : 0;
 
	//  % de avance del mes (cumplimiento temporal)
	const today = new Date(dateTo);
	const year = today.getFullYear();
	const month = today.getMonth();

	// días totales del mes
	const daysInMonth = new Date(year, month + 1, 0).getDate();

	// día actual del mes
	const daysElapsed = today.getDate();

	// % de avance temporal
	const pctAvanceMes =
	  daysInMonth > 0 ? (daysElapsed / daysInMonth) * 100 : 0; 
 
return (
    <div className="p-3" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 fw-bold" style={{ color: '#1B2A4A' }}>
          Dashboard de Resultados — Ford
        </h4>
        <div className="d-flex gap-2 align-items-center">
          <Form.Control type="date" size="sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 140 }} />
          <span className="text-muted">a</span>
          <Form.Control type="date" size="sm" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 140 }} />
        </div>
      </div>

      {/* FASE 1: Resumen Consolidado */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Header className="py-2 text-white fw-bold" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #2c3e6b 100%)' }}>
          FASE 1: Resumen Consolidado
        </Card.Header>
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col xs={12} md={4}><KpiCard title="Inversión Real" value={fmtCurrency(invReal)} color="#1B2A4A" /></Col>
            <Col xs={12} md={4}><KpiCard title="Presupuesto" value={fmtCurrency(invBudgeted)} subtitle={invBudgeted > 0 ? `${fmt(investmentCompliance?.totalOrders || 0)} órdenes activas` : 'Sin órdenes'} color="#4dabf7" /></Col>
            <Col xs={12} md={4}><KpiCard title="KPIs Reales" value={`${fmt(totalLeads)} Leads`} subtitle={leadsObj ? `Obj: ${fmt(leadsObj.objective)}` : `${fmt(totalClicks)} Clicks`} color="#51cf66" /></Col>
          </Row>
          <Row className="g-3">
			<Col md={7}>
				<Card className="border h-100">
				  <Card.Body>
					<div className="fw-semibold small mb-2" style={{ color: '#1B2A4A' }}>
					  Evolutivo Diario de Leads y CPL
					</div>
					<DailyLeadsCplChart data={data.dailyTrend} />
				  </Card.Body>
				</Card>
			</Col>
            <Col md={5}>
              <Card className="border h-100">
                <Card.Body>
				  <div className="fw-semibold small mb-2 text-center" style={{ color: '#1B2A4A' }}>% de Cumplimiento</div>
				  <div className="d-flex justify-content-around flex-wrap">

					<GaugeChart
					  value={cumplimientoInversion}
					  label="Inversión"
					  color={cumplimientoInversion >= 90 && cumplimientoInversion <= 110 ? '#51cf66' : cumplimientoInversion >= 70 ? '#fcc419' : '#ff6b6b'}
					/>

					<GaugeChart
					  value={cumplimientoKpi}
					  label="Leads"
					  color={cumplimientoKpi >= 90 && cumplimientoKpi <= 110 ? '#51cf66' : cumplimientoKpi >= 70 ? '#fcc419' : '#ff6b6b'}
					/>

				  </div>

				  {objectives.length > 0 && (
					<div className="text-center mt-1">
					  <small className="text-muted" style={{ fontSize: 9 }}>
						Basado en {objectives.length} objetivos configurados
					  </small>
					</div>
				  )}
				</Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* FASE 2: Cumplimiento por Modelo de Autos 
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Header className="py-2 text-white fw-bold" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #2c3e6b 100%)' }}>
          FASE 2: Cumplimiento por Modelo de Autos
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
              <thead style={{ background: '#f1f3f5' }}>
                <tr>
                  <th className="ps-3">Modelo</th>
                  <th className="text-end">Inversión</th>
                  <th className="text-center" style={{ minWidth: 130 }}>% Cumplimiento</th>
                  <th className="text-end">KPI (Leads)</th>
                  <th className="text-end">Clicks</th>
                  <th className="text-end">Impresiones</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byModelo).length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted py-3">No se detectaron modelos en nombres de campaña</td></tr>
                ) : (
                  Object.entries(byModelo)
                    .sort(([, a], [, b]) => b.cost - a.cost)
                    .map(([modelo, d]) => {
                      const invTarget = invBudgeted > 0 ? invBudgeted : totalCost;
                      const pctInv = invTarget > 0 ? (d.cost / (invTarget / Object.keys(byModelo).length)) * 100 : 0;
                      const barColor = pctInv >= 90 ? '#51cf66' : pctInv >= 70 ? '#fcc419' : '#ff6b6b';
                      return (
                        <tr key={modelo}>
                          <td className="ps-3 fw-semibold">{modelo}</td>
                          <td className="text-end">{fmtCurrency(d.cost)}</td>
                          <td><ProgressBar value={Math.min(pctInv, 100)} color={barColor} /></td>
                          <td className="text-end">{fmt(d.leads)}</td>
                          <td className="text-end">{fmt(d.clicks)}</td>
                          <td className="text-end">{fmt(d.impressions)}</td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>*/}

      {/* FASE 2: Cumplimiento por Plataforma de Pauta */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Header className="py-2 text-white fw-bold" style={{ background: 'linear-gradient(135deg, #1B2A4A 0%, #2c3e6b 100%)' }}>
          FASE 2: Cumplimiento por Plataforma de Pauta
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                  <thead style={{ background: '#f1f3f5' }}>
                    <tr>
                      <th className="ps-3">Plataforma</th>
                      <th className="text-end">Inversión</th>
                      <th className="text-center" style={{ minWidth: 110 }}>% Cumplimiento</th>
                      <th className="text-end">KPI (Leads)</th>
                      <th className="text-end">Impresiones</th>
                      <th className="text-end">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
					  {Object.entries(byPlatform)
						.sort(([, a], [, b]) => b.cost - a.cost)
						.map(([plat, d]) => {
						  const pct = pctAvanceMes;
						  const color = PLATFORM_COLORS[plat] || '#748194';

						  return (
							<tr key={plat}>
							  <td className="ps-3">
								<Badge
								  bg="light"
								  text="dark"
								  className="border"
								  style={{ borderLeft: `3px solid ${color}` }}
								>
								  {plat === 'META'
									? 'Meta Ads'
									: plat === 'GOOGLE'
									? 'Google Ads'
									: plat === 'TIKTOK'
									? 'TikTok Ads'
									: plat === 'LINKEDIN'
									? 'LinkedIn Ads'
									: plat}
								</Badge>
							  </td>

							  <td className="text-end">{fmtCurrency(d.cost)}</td>

							  <td>
								<ProgressBar value={Math.min(pct, 100)} color={color} />
							  </td>

							  <td className="text-end">{fmt(d.leads)}</td>
							  <td className="text-end">{fmt(d.impressions)}</td>
							  <td className="text-end">{fmt(d.clicks)}</td>
							</tr>
						  );
						})}
					</tbody>
                </table>
              </div>
            </Col>
            <Col md={4}>
			  <div className="fw-semibold small mb-2 text-center" style={{ color: '#1B2A4A' }}>
				% Cumplimiento KPIs (Leads vs objetivo mensual)
			  </div>

			  <div className="d-flex flex-wrap justify-content-center gap-2">
				{Object.entries(byPlatform)
				  .sort(([, a], [, b]) => b.leads - a.leads)
				  .map(([plat, d]) => {

					const globalObjective = aggregated?.Leads?.objective || 0;
					const totalInvestment = totalCost || 0;

					// objetivo derivado por plataforma (proporcional a inversión)
					const platformObjective =
					  totalInvestment > 0
						? globalObjective * (d.cost / totalInvestment)
						: 0;

					// % cumplimiento real vs objetivo derivado
					const pct =
					  platformObjective > 0
						? (d.leads / platformObjective) * 100
						: 0;

					const label =
					  plat === 'META'
						? 'Meta'
						: plat === 'GOOGLE'
						? 'Google'
						: plat === 'TIKTOK'
						? 'TikTok'
						: plat === 'LINKEDIN'
						? 'LinkedIn'
						: plat;

					return (
					  <MiniDonut
						key={plat}
						value={Math.min(pct, 100)}
						color={PLATFORM_COLORS[plat] || '#748194'}
						label={label}
					  />
					);
				  })}

			  </div>
			</Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FordResultadosDashboard;
